// =============================================================================
// notifications-client.ts — Contrat du client NOTIFICATIONS FOURNISSEUR (R15-COM-06).
// =============================================================================
// Miroir des routes @SupplierRoute du BC communication (centre de notifications in-app, DBU §3.8 /
// §1.4.9). L'AUTORITÉ est l'API : tri récence, keyset, exclusion du chat du volet consolidé (G-4),
// recalcul du badge non-lus. Ce client ne porte AUCUNE règle métier — il appelle, parse, mappe l'erreur.
//
// SÉCURITÉ (RULE_REF_006) : la session fournisseur (cookie httpOnly `supplier_session`) n'est JAMAIS lue
// ni fabriquée ici ; elle est portée par le cookie (credentials: 'include'). Le siret vient de la session
// côté API (RLS). Aucune donnée métier en localStorage. Horodatages = ISO 8601 (string).

/**
 * Code couleur métier (énum backend `NotificationColorCode`). Piloté par catégorie côté serveur. L'UI le
 * mappe vers un TOKEN du Design System (jamais une couleur hex en dur) — cf. `notification-presentation`.
 * Le champ DTO reste `string` (résilience : une valeur inconnue retombe sur un tone neutre + libellé).
 */
export type NotificationColorCode = 'Bleu' | 'Orange' | 'Rouge' | 'Vert' | 'Gris';

/**
 * Catégorie d'une notification (énum backend). `NouveauMsgChat` est COMPTÉ dans le badge mais EXCLU du
 * volet consolidé (G-4 : le chat se consulte dans la Messagerie ; l'endpoint list l'exclut déjà serveur).
 */
export type NotificationCategory =
  | 'LancementCampagne'
  | 'RappelJ3'
  | 'RappelJ1'
  | 'Validation'
  | 'Phasage'
  | 'ReouvertureNego'
  | 'Annonce'
  | 'NouveauMsgChat';

/**
 * Élément de notification (parité DTO API). `payloadContext` porte des IDENTIFIANTS (campaignId, cycle,
 * submissionId, threadId, messageId, announcementId) qui servent la NAVIGATION au clic — jamais du contenu
 * nominatif affiché tel quel. `category`/`colorCode` restent `string` (tolérance aux valeurs futures).
 */
export interface NotificationItemDto {
  readonly notificationId: string;
  readonly category: string;
  readonly colorCode: string;
  readonly triggeredAt: string;
  readonly readAt: string | null;
  readonly payloadContext: Record<string, unknown>;
}

/** Réponse du résumé (cloche + widget D-04) : compteur non-lus + 3 derniers NON-LUS (toutes catégories). */
export interface NotificationSummaryDto {
  readonly unreadCount: number;
  readonly latest: readonly NotificationItemDto[];
}

/** Page du volet consolidé (keyset). `nextCursor` opaque ; `null` = fin de liste. EXCLUT le chat (serveur). */
export interface NotificationPageDto {
  readonly items: readonly NotificationItemDto[];
  readonly nextCursor: string | null;
}

/** Ack d'une mutation de lecture : le badge RECALCULÉ par le serveur (autorité). */
export interface NotificationUnreadCountDto {
  readonly unreadCount: number;
}

/** Genre d'erreur applicative, mappé pour un message UX explicite (par STATUS HTTP). */
export type NotificationApiErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'not-found'
  | 'generic';

/**
 * Erreur applicative renvoyée par l'API notifications (HTTP 4xx/5xx). L'UI l'affiche telle quelle ; elle
 * ne réinterprète JAMAIS la règle métier (autorité = API). Le `kind` permet un libellé UX ciblé.
 */
export class NotificationApiError extends Error {
  public readonly kind: NotificationApiErrorKind;

  public constructor(
    message: string,
    kind: NotificationApiErrorKind = 'generic',
  ) {
    super(message);
    this.name = 'NotificationApiError';
    this.kind = kind;
  }
}

/**
 * Contrat du client notifications FOURNISSEUR — miroir des routes @SupplierRoute. LECTURES (summary/list)
 * sans CSRF ; MUTATION (read en LOT) avec double-submit CSRF (`supplier_csrf`). L'implémentation réelle
 * est HTTP ; les tests injectent un double via le DI seam (NotificationsClientProvider).
 *
 * DBU D-1/D-2 : PAS de « tout marquer lu » (retiré V1) ni de marquage lu au clic. Le marquage est un LOT
 * AUTOMATIQUE des ids AFFICHÉS non-lus à l'ouverture du volet (clause système du centre, DBU l.3252).
 */
export interface NotificationsClient {
  /** GET résumé (compteur non-lus + 3 derniers non-lus, toutes catégories). Sert la cloche ET le widget. */
  getSummary(): Promise<NotificationSummaryDto>;
  /** GET une page du volet consolidé (keyset). `cursor` absent = première page ; EXCLUT le chat (serveur). */
  listPage(cursor?: string): Promise<NotificationPageDto>;
  /**
   * POST marque un LOT de notifications lues (ids affichés non-lus, auto-read à l'affichage du volet, DBU
   * l.3252) ; renvoie le badge recalculé. MUTATION ⇒ CSRF. `ids` vide = aucune action attendue côté appelant.
   */
  markRead(ids: readonly string[]): Promise<NotificationUnreadCountDto>;
}
